import { AspectRatio, Box, Button, ChakraProps, Flex, Text } from '@chakra-ui/react';
import { colorScheme } from 'lib/colorScheme';
import { LinkType } from 'lib/enums/LinkType';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FunctionComponent } from 'react';
import { OverviewItemModel } from 'utils/types/overviewItemModel';

import { Link as CustomLink } from '../base/Link';
import { Tag, tags, TagType } from '../base/Tag';
import { HyphenResult } from '../hyphen/hyphenation';

interface OverviewItemProps {
  model: OverviewItemModel;
  singleItem?: boolean;
  imageSx?: ChakraProps;
  numberOfItems?: number;
  enableHyphenHeading?: boolean;
  enableHyphenSummary?: boolean;
  hideTag?: string;
  backgroundColor?: string;
}

export const OverviewItem: FunctionComponent<OverviewItemProps> = ({
  model,
  singleItem,
  numberOfItems,
  enableHyphenHeading,
  enableHyphenSummary,
  hideTag,
  backgroundColor,
}) => {
  const router = useRouter();
  // Use the passed backgroundColor only for single items, otherwise use white
  const dynamicBackgroundColor = singleItem ? backgroundColor || 'white' : 'white';
  const mediaPosition = model.mediaPosition || 'end';

  if (singleItem) {
    // For single items, create a layout similar to FeaturedContent
    return (
      <Box position='relative' w='100%' mx='auto'>
        <Flex
          direction={{
            base: 'column-reverse',
            lg: 'row',
          }}
          pt={{ base: 'xs', md: 's', lg: 'xs' }}
          pb={{ base: 's', md: 'm', lg: 'xs' }}
          bg={singleItem && model.backgroundWidth === 'full' ? dynamicBackgroundColor : 'transparent'}
        >
          {/* Text Content */}
          <Box
            w={{ base: '100%', lg: 'calc(30% + 80px)' }}
            zIndex={1}
            position='relative'
            pl={mediaPosition === 'end' ? { lg: 's' } : { lg: 's' }}
            // pr={mediaPosition === 'start' ? { lg: 'l' } : undefined}
            order={{ lg: mediaPosition === 'start' ? 2 : 1 }} // Text: order 2 when media starts, order 1 when media ends
          >
            {/* Headline */}
            {model.headline && (
              <Text
                as='h3'
                textStyle={{ base: 'mobile.h4', md: 'desktop.h4' }}
                pb={{ base: 'xs' }}
                dangerouslySetInnerHTML={{
                  __html: model.headline
                    ? model.enableHyphenation == 'yes'
                      ? HyphenResult(model.headline, router.locale)
                      : model.headline
                    : '',
                }}
              />
            )}

            {/* Description */}
            <Flex flexDirection='column' mt='s'>
              {model.text?.map((text, index) => (
                <Text
                  key={index}
                  mb={model.text && index === model.text.length - 1 ? 'xs' : 0}
                  textStyle={{ base: 'mobile.body.s', md: 'desktop.body.l' }}
                  dangerouslySetInnerHTML={{
                    __html: text.body
                      ? model.enableHyphenation == 'yes'
                        ? HyphenResult(text.body, router.locale)
                        : text.body
                      : '',
                  }}
                />
              ))}
            </Flex>

            {/* CTA */}
            {model.linkText && (
              <Box mt='s'>
                {model.link && model.ctaSnippetStyle != 'button' ? (
                  <CustomLink
                    ariaLabel={model.ariaLinkText}
                    href={'/[...slug].tsx'}
                    as={`${model.link}`}
                    type={LinkType.SecondaryButton}
                    iconName='chevronRightForLink'
                  >
                    {model.linkText}
                  </CustomLink>
                ) : model.link && model.ctaSnippetStyle == 'button' ? (
                  <Button as='a' target={model.linkOpenInNewTab ? '_blank' : ''} href={model.link}>
                    {model.linkText}
                  </Button>
                ) : model.externalUrl && model.ctaSnippetStyle != 'button' ? (
                  <CustomLink
                    href={model.externalUrl}
                    type={LinkType.SecondaryButton}
                    iconName='chevronRightForLink'
                    isExternal={model.linkOpenInNewTab}
                  >
                    {model.linkText}
                  </CustomLink>
                ) : model.externalUrl && model.ctaSnippetStyle == 'button' ? (
                  <Button as='a' target={model.linkOpenInNewTab ? '_blank' : ''} href={model.externalUrl}>
                    {model.linkText}
                  </Button>
                ) : model.downloadLink && model.ctaSnippetStyle != 'button' ? (
                  <CustomLink
                    href={model.downloadLink}
                    type={LinkType.SecondaryButton}
                    download={model.downloadLink}
                    iconName='chevronRightForLink'
                    isExternal={model.linkOpenInNewTab}
                  >
                    {model.linkText}
                  </CustomLink>
                ) : (
                  model.downloadLink &&
                  model.ctaSnippetStyle == 'button' && (
                    <Button
                      as='a'
                      target={model.linkOpenInNewTab ? '_blank' : ''}
                      href={model.downloadLink}
                      download={model.downloadLink}
                    >
                      {model.linkText}
                    </Button>
                  )
                )}
              </Box>
            )}
          </Box>

          {/* Image Content - with overlap effect */}
          {model.image && (
            <Box
              w={{ base: '100%', lg: '780px' }}
              h={{ base: 'auto', lg: '438px' }}
              position='relative'
              zIndex={0}
              pl={singleItem ? { base: 'xs', lg: 's' } : 0}
              order={{ lg: mediaPosition === 'start' ? 1 : 2 }} // Image: order 1 when media starts, order 2 when media ends
            >
              <AspectRatio ratio={16 / 9} w='100%' overflow='hidden'>
                {model.link ? (
                  <CustomLink
                    ariaLabel={model.ariaLinkText}
                    href={'/[...slug].tsx'}
                    as={`${model.link}`}
                    type={LinkType.Item}
                  >
                    {model.image && (
                      <Image
                        width={model.image.width}
                        height={model.image.height}
                        src={model.image.src}
                        alt={model.image.alt}
                        sizes={'(max-width: 1024px) 100vw, 50vw'}
                      />
                    )}
                  </CustomLink>
                ) : model.externalUrl ? (
                  <CustomLink href={model.externalUrl} type={LinkType.Item} isExternal={model.linkOpenInNewTab}>
                    {model.image && (
                      <Image
                        width={model.image.width}
                        height={model.image.height}
                        src={model.image.src}
                        alt={model.image.alt}
                        quality={model.image.quality}
                      />
                    )}
                  </CustomLink>
                ) : model.downloadLink ? (
                  <CustomLink href={model.downloadLink} type={LinkType.Item} isExternal={model.linkOpenInNewTab}>
                    {model.image && (
                      <Image
                        width={model.image.width}
                        height={model.image.height}
                        src={model.image.src}
                        alt={model.image.alt}
                        quality={model.image.quality}
                      />
                    )}
                  </CustomLink>
                ) : (
                  <Image
                    width={model.image.width}
                    height={model.image.height}
                    src={model.image.src}
                    alt={model.image.alt}
                    quality={model.image.quality}
                  />
                )}
              </AspectRatio>

              {/* Tags */}
              {(model.featured || model.mediaType) && (
                <Flex wrap='wrap' justifyContent='end' gap='xxs' position='absolute' right='xs' bottom='s' id='tag'>
                  {model.mediaType && tags.includes(model.mediaType) && model.link && (
                    <a href={model.link}>
                      <Tag type={model.mediaType as TagType} />
                    </a>
                  )}
                  {model.featured && <Tag type='featured' />}
                </Flex>
              )}
            </Box>
          )}
        </Flex>
      </Box>
    );
  }

  // Original OverviewItem for multiple items
  return (
    <Box
      display='flex'
      flexDir='column-reverse'
      w={{ base: numberOfItems === 3 ? '71.2vw' : 'unset', lg: 'unset' }}
      minW={{ base: '71.2vw', lg: '200px' }}
      h='100%'
      justifyContent={'start'}
    >
      <Flex ml={0} flexDir={'column'} wordBreak='break-word'>
        {/* Image block start */}
        {model.image && (
          <Box w='100%' position='relative' mb={{ base: 'xs', lg: model.metadata ? 'xs' : 's' }}>
            {model.link && (
              <CustomLink
                ariaLabel={model.ariaLinkText}
                href={'/[...slug].tsx'}
                as={`${model.link}`}
                type={LinkType.Item}
              >
                <AspectRatio w='100%' ratio={16 / 9} mt={{ base: 's', md: 'l' }}>
                  <Image
                    width={model.image.width}
                    height={model.image.height}
                    src={model.image.src}
                    alt={model.image.alt}
                    sizes={'(max-width: 1024px) 100vw, 50vw'}
                  />
                </AspectRatio>{' '}
              </CustomLink>
            )}
            {model.externalUrl && (
              <AspectRatio ratio={16 / 9} maxW='1276px' overflow='hidden' mt={{ base: 's', md: 'l' }}>
                <CustomLink href={model.externalUrl} type={LinkType.Item} isExternal={model.linkOpenInNewTab}>
                  <Image
                    width={model.image.width}
                    height={model.image.height}
                    src={model.image.src}
                    alt={model.image.alt}
                    quality={model.image.quality}
                  />
                </CustomLink>
              </AspectRatio>
            )}
            {model.downloadLink ? (
              <AspectRatio ratio={16 / 9} maxW='1276px' overflow='hidden' mt={{ base: 's', md: 'l' }}>
                <CustomLink href={model.downloadLink} type={LinkType.Item} isExternal={model.linkOpenInNewTab}>
                  <Image
                    width={model.image.width}
                    height={model.image.height}
                    src={model.image.src}
                    alt={model.image.alt}
                    quality={model.image.quality}
                  />
                </CustomLink>
              </AspectRatio>
            ) : (
              !model.link &&
              !model.externalUrl && (
                <AspectRatio ratio={16 / 9} maxW='1276px' overflow='hidden' mt={{ base: 's', md: 'l' }}>
                  <Image
                    width={model.image.width}
                    height={model.image.height}
                    src={model.image.src}
                    alt={model.image.alt}
                    quality={model.image.quality}
                  />
                </AspectRatio>
              )
            )}

            {/* Tags */}
            {(model.featured || model.mediaType) && (
              <Flex wrap='wrap' justifyContent='end' gap='xxs' position='absolute' right='xs' bottom='s' id='tag'>
                {model.mediaType && tags.includes(model.mediaType) && model.link && (
                  <a href={model.link}>
                    <Tag type={model.mediaType as TagType} />
                  </a>
                )}
                {model.featured && <Tag type='featured' />}
              </Flex>
            )}
          </Box>
        )}

        {model.metadata && hideTag != 'yes' && (
          <Text suppressHydrationWarning id='tag-data' mb={{ base: '0px', md: 'xxs' }} textStyle='desktop.body.book.s'>
            {model.metadata}
          </Text>
        )}

        {/* Headline block */}
        {model.link ? (
          <CustomLink ariaLabel={model.ariaLinkText} href={'/[...slug].tsx'} as={`${model.link}`} type={LinkType.Item}>
            <Text
              as='h3'
              textStyle={{ base: 'mobile.h4', md: 'desktop.h4' }}
              pb={{ base: 'xs' }}
              dangerouslySetInnerHTML={{
                __html: model.headline
                  ? model.enableHyphenation == 'yes'
                    ? HyphenResult(model.headline, router.locale)
                    : model.headline
                  : '',
              }}
            />
          </CustomLink>
        ) : model.externalUrl ? (
          <CustomLink href={model.externalUrl} type={LinkType.Item} isExternal={model.linkOpenInNewTab}>
            <Text
              as='h3'
              textStyle={{ base: 'mobile.h4', md: 'desktop.h4' }}
              pb={{ base: 'xs' }}
              dangerouslySetInnerHTML={{
                __html: model.headline
                  ? model.enableHyphenation == 'yes'
                    ? HyphenResult(model.headline, router.locale)
                    : model.headline
                  : '',
              }}
            />
          </CustomLink>
        ) : model.downloadLink ? (
          <CustomLink
            href={model.downloadLink}
            type={LinkType.Item}
            download={model.downloadLink}
            isExternal={model.linkOpenInNewTab}
          >
            <Text
              as='h3'
              textStyle={{ base: 'mobile.h4', md: 'desktop.h4' }}
              pb={{ base: 'xs' }}
              dangerouslySetInnerHTML={{
                __html: model.headline
                  ? model.enableHyphenation == 'yes'
                    ? HyphenResult(model.headline, router.locale)
                    : model.headline
                  : '',
              }}
            />
          </CustomLink>
        ) : (
          <Text
            as='h3'
            textStyle={{ base: 'mobile.h4', md: 'desktop.h4' }}
            pb={{ base: 'xs' }}
            dangerouslySetInnerHTML={{
              __html: model.headline
                ? model.enableHyphenation == 'yes'
                  ? HyphenResult(model.headline, router.locale)
                  : model.headline
                : '',
            }}
          />
        )}

        {/* Description and CTA */}
        <Flex flexDirection={'column'}>
          {/* Text content */}
          {model.text?.map((text, index) => {
            if (model.link) {
              return (
                <CustomLink
                  key={index}
                  ariaLabel={model.ariaLinkText}
                  href={'/[...slug].tsx'}
                  as={`${model.link}`}
                  type={LinkType.Item}
                >
                  <Text
                    mb={model.text && index === model.text.length - 1 ? 'xs' : 0}
                    textStyle={{ base: 'mobile.body.s', md: 'desktop.body.l' }}
                    dangerouslySetInnerHTML={{
                      __html: text.body
                        ? model.enableHyphenation == 'yes'
                          ? HyphenResult(text.body, router.locale)
                          : text.body
                        : '',
                    }}
                  />
                </CustomLink>
              );
            }
            if (model.externalUrl) {
              return (
                <CustomLink
                  key={index}
                  href={model.externalUrl}
                  type={LinkType.Item}
                  isExternal={model.linkOpenInNewTab}
                >
                  <Text
                    mb={model.text && index === model.text.length - 1 ? 'xs' : 0}
                    textStyle={{ base: 'mobile.body.s', md: 'desktop.body.l' }}
                    dangerouslySetInnerHTML={{
                      __html: text.body
                        ? model.enableHyphenation == 'yes'
                          ? HyphenResult(text.body, router.locale)
                          : text.body
                        : '',
                    }}
                  />
                </CustomLink>
              );
            }
            if (model.downloadLink) {
              return (
                <CustomLink
                  key={index}
                  href={model.downloadLink}
                  type={LinkType.Item}
                  download={model.downloadLink}
                  isExternal={model.linkOpenInNewTab}
                >
                  <Text
                    mb={model.text && index === model.text.length - 1 ? 'xs' : 0}
                    textStyle={{ base: 'mobile.body.s', md: 'desktop.body.l' }}
                    dangerouslySetInnerHTML={{
                      __html: text.body
                        ? model.enableHyphenation == 'yes'
                          ? HyphenResult(text.body, router.locale)
                          : text.body
                        : '',
                    }}
                  />
                </CustomLink>
              );
            }
            return (
              <Text
                key={index}
                mb={model.text && index === model.text.length - 1 ? 'xs' : 0}
                textStyle={{ base: 'mobile.body.s', md: 'desktop.body.l' }}
                dangerouslySetInnerHTML={{
                  __html: text.body
                    ? model.enableHyphenation == 'yes'
                      ? HyphenResult(text.body, router.locale)
                      : text.body
                    : '',
                }}
              />
            );
          })}

          {/* CTA links */}
          {model.link && model.linkText && model.ctaSnippetStyle != 'button' && (
            <CustomLink
              ariaLabel={model.ariaLinkText}
              href={'/[...slug].tsx'}
              as={`${model.link}`}
              type={LinkType.SecondaryButton}
              iconName='chevronRightForLink'
            >
              {model.linkText}
            </CustomLink>
          )}
          {model.link && model.linkText && model.ctaSnippetStyle == 'button' && (
            <Button as='a' target={model.linkOpenInNewTab ? '_blank' : ''} href={model.externalUrl}>
              {model.linkText}
            </Button>
          )}
          {model.downloadLink && model.linkText && model.ctaSnippetStyle != 'button' && (
            <CustomLink
              href={model.downloadLink}
              type={LinkType.SecondaryButton}
              download={model.downloadLink}
              iconName='chevronRightForLink'
              isExternal={model.linkOpenInNewTab}
            >
              {model.linkText}
            </CustomLink>
          )}
          {model.downloadLink && model.linkText && model.ctaSnippetStyle == 'button' && (
            <Button
              as='a'
              target={model.linkOpenInNewTab ? '_blank' : ''}
              href={model.downloadLink}
              download={model.downloadLink}
            >
              {model.linkText}
            </Button>
          )}
          {model.externalUrl && model.linkText && model.ctaSnippetStyle != 'button' && (
            <CustomLink
              href={model.externalUrl}
              type={LinkType.SecondaryButton}
              iconName='chevronRightForLink'
              isExternal={model.linkOpenInNewTab}
            >
              {model.linkText}
            </CustomLink>
          )}
          {model.externalUrl && model.linkText && model.ctaSnippetStyle == 'button' && (
            <Button as='a' target={model.linkOpenInNewTab ? '_blank' : ''} href={model.externalUrl}>
              {model.linkText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
